import {
    Component,
    Input,
    Output,
    EventEmitter,
    ElementRef,
    ViewChild,
    HostListener,
    Inject,
    OnDestroy,
    forwardRef,
    OnInit
} from '@angular/core';
import {
    ControlValueAccessor,
    NG_VALUE_ACCESSOR,
    NG_VALIDATORS,
    Validator,
    AbstractControl,
    ValidationErrors
} from '@angular/forms';
import { THEME_TOKEN, Theme } from '../types/theme.types';
import { autocompleteStylesSAG, autocompleteStylesSHS } from './remote-autocomplete.styles';
import { componentStylesByTheme } from '../utils/theme.utils';
import { Subject, Subscription } from 'rxjs';
import { debounceTime, finalize, tap } from 'rxjs/operators';
import { SearchFn, SearchFnObservable } from './types/search-fn.type';

@Component({
    selector: 'sag-ui-remote-autocomplete',
    templateUrl: './remote-autocomplete.component.html',
    styleUrls: [],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => RemoteAutocompleteComponent),
            multi: true
        },
        {
            provide: NG_VALIDATORS,
            useExisting: forwardRef(() => RemoteAutocompleteComponent),
            multi: true
        }
    ]
})
export class RemoteAutocompleteComponent<T> implements ControlValueAccessor, Validator, OnDestroy, OnInit {
    @Input() id = this.randomId();
    @Input() placeholder = '';
    @Input() required = false;
    @Input() disabled = false;
    @Input() readOnly = false;
    @Input() label = '';
    @Input() value: T | null = null;
    @Input() invalid = false;
    @Input() error: string | string[] = '';
    @Input() hint?: string;
    @Input() ariaLabel: string | null = null;
    @Input() ariaLabelledby: string | null = null;
    @Input() ariaDescribedby: string | null = null;
    @Input() options: { label: string; value: T }[] = [];
    @Input()
    searchFn: SearchFn = async () => void 0;
    @Input()
    searchFn$: SearchFnObservable;
    @Input()
    compareWith: (o1: T | null, o2: T | null) => boolean = (o1, o2) => {
        if (typeof o1 === 'object' && typeof o2 === 'object') {
            return JSON.stringify(o1) === JSON.stringify(o2);
        }
        return o1 === o2;
    };

    @Output() change: EventEmitter<T | null> = new EventEmitter<T | null>();
    @Output() click = new EventEmitter<Event>();
    @Output() focus = new EventEmitter<Event>();
    @Output() blur = new EventEmitter<Event>();
    @Output() keydown = new EventEmitter<KeyboardEvent>();
    @Output() keyup = new EventEmitter<KeyboardEvent>();
    @Output() mouseenter = new EventEmitter<MouseEvent>();
    @Output() mouseleave = new EventEmitter<MouseEvent>();

    @ViewChild('input') input!: ElementRef;

    @HostListener('document:mousedown', ['$event'])
    onMouseDown(event: MouseEvent): void {
        const clickedInside = this.el.nativeElement.contains(event.target);
        if (!clickedInside) {
            this.isOpen = false;
        }
    }

    @HostListener('focusout', ['$event'])
    onFocusOut(event: FocusEvent): void {
        const focusedInside = this.el.nativeElement.contains(event.relatedTarget);
        if (!focusedInside) {
            this.isOpen = false;
        }
    }

    protected get popoverClasses(): string {
        const popoverStyles = componentStylesByTheme(this.theme, autocompleteStylesSAG, autocompleteStylesSHS);
        return popoverStyles();
    }

    protected get displayValue(): string {
        return this.selectedItem ? this.selectedItem.label : '';
    }

    protected touched = false;
    protected selectedItem: { label: string; value: any } | null = null;
    protected isOpen = false;
    protected filterValue = '';
    protected searchSubject: Subject<string> = new Subject<string>();
    protected currentPage = 0;
    protected loading = false;

    private subscriptions: Subscription[] = [];

    constructor(@Inject(THEME_TOKEN) private readonly theme: Theme, private el: ElementRef) {}

    ngOnInit(): void {
        this.initSearchSubject();
        this.initSelectedItems();
        this.loadOptions('', 1);
    }

    ngOnDestroy(): void {
        this.subscriptions.forEach(sub => sub.unsubscribe());
    }

    protected onFocus() {
        if (this.disabled || this.readOnly) return;
        this.isOpen = true;
    }

    protected onBlur() {
        this.markAsTouched();
    }

    protected togglePopover(event: Event): void {
        event.stopPropagation();
        event.preventDefault();
        if (this.isOpen) {
            this.isOpen = false;
            this.input.nativeElement.blur();
        } else {
            this.isOpen = true;
            this.input.nativeElement.focus();
        }
    }

    protected handleInput(event: Event): void {
        const target = event.target as HTMLInputElement;
        this.filterValue = target.value;
        this.searchSubject.next(this.filterValue);
    }

    protected handleChange(): void {
        this.selectedItem = null;
        this.writeValue(null);
    }

    protected handleOptionSelect(data: [string, any]): void {
        const [label, value] = data;
        this.input.nativeElement.focus();
        this.markAsTouched();
        if (this.compareWith(this.value, value)) {
            this.selectedItem = null;
            this.writeValue(null);
        } else {
            this.selectedItem = { label, value };
            this.writeValue(value);
        }
        this.isOpen = false;
        setTimeout(() => {
            this.input.nativeElement.blur();
        }, 50);
    }

    protected onScroll(event: Event): void {
        const target = event.target as HTMLElement;
        if (target.scrollHeight - target.scrollTop <= target.clientHeight + 1) {
            this.currentPage++;
            this.loadOptions(this.filterValue, this.currentPage);
        }
    }

    protected isArray(value: string | string[]): value is string[] {
        return Array.isArray(value);
    }

    // ControlValueAccessor implementation
    onChange = (_value: any) => {};
    onTouch = () => {};

    public registerOnChange(fn: any): void {
        this.onChange = fn;
    }

    public registerOnTouched(fn: any): void {
        this.onTouch = fn;
    }

    public writeValue(val: T | null): void {
        this.value = val;
        this.onChange(this.value);
        this.change.emit(this.value);
    }

    public markAsTouched() {
        if (!this.touched) {
            this.onTouch();
            this.touched = true;
        }
    }

    public setDisabledState(disabled: boolean): void {
        this.disabled = disabled;
    }

    public validate(_: AbstractControl<any, any>): ValidationErrors | null {
        return this.required && !this.selectedItem ? { required: true } : null;
    }

    private randomId(): string {
        return Math.random().toString(36).substring(2);
    }

    private initSearchSubject() {
        this.subscriptions.push(
            this.searchSubject
                .pipe(
                    debounceTime(500),
                    tap(searchText => {
                        this.currentPage = 0;
                        this.loadOptions(searchText, this.currentPage);
                    })
                )
                .subscribe()
        );
    }

    private initSelectedItems() {
        if (this.value) {
            this.selectedItem = this.options.find(option => this.compareWith(this.value, option.value)) || null;
        }
    }

    private async loadOptions(search: string, page: number): Promise<void> {
        this.loading = true;

        if (this.searchFn) {
            try {
                await this.searchFn(search, page);
            } finally {
                this.loading = false;
            }
        }

        if (this.searchFn$) {
            this.searchFn$(search, page).pipe(finalize(() => (this.loading = false)));
        }
    }
}
